import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import OrderTable from "../../components/Table/OrderTable";
import { IoSearchSharp } from "react-icons/io5";
import Card from "../../components/Table/Card";
import { useThunk } from "../../hooks/useThunk";
import { fetchUserOrders } from "../../store";
import { useSelector } from "react-redux";
import RecordNotFound from "../../components/Table/RecordNotFound";
import showToast from "../../utils/toastNotifications";
import Pagination from "../../components/Table/pagination";
import { orderStatus } from "../../constants/commonConstants";
import FetchingError from "../../components/Table/FetchingError";

const InOProgressOrders = () => {
  const navigate = useNavigate();
  const { orders: pendingOrders, pagination: getPagination } = useSelector(
    (state) => state.createOrder?.inProgressOrders
  );

  // Thunks
  const [doUserOrdersFetch, isUserOrdersFetching, userOrdersError] =
    useThunk(fetchUserOrders);

  // State variables
  const itemsPerPage = 6;
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(
    Number(searchParams.get("page")) || 1
  );
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || ""
  );

  const [isSearchTriggered, setIsSearchTriggered] = useState(false);

  useEffect(() => {
    const params = {
      page: currentPage,
      search: searchTerm,
    };
    setSearchParams(params);
    setCurrentPage(currentPage);
    setSearchTerm(searchTerm);
  }, [setSearchParams, currentPage, searchTerm]);

  useEffect(() => {
    // Only fetch orders if not triggered by a search action
    if (!isSearchTriggered) {
      fetchUserOrder();
    } else {
      setIsSearchTriggered(false);
    }
  }, [currentPage]);

  const fetchUserOrder = async (term = searchTerm, page = currentPage) => {
    const offset = (page - 1) * itemsPerPage;
    const limit = itemsPerPage;
    const status = [
        orderStatus.IN_REVISION, 
        orderStatus.ORDER_IN_PROGRESS, 
        orderStatus.PENDING_WRITER, 
        orderStatus.ORDER_DELIVERED
    ]
    const result = await doUserOrdersFetch({ 
      offset, 
      limit, 
      multipleOrderStatus: status.join(","), 
      searchTerm: term
    });
    if (!result.success) {
      console.log(result?.error?.message || "Error fetching user orders");
      showToast("error", result?.error?.message);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleViewOrder = (orderID) => {
    console.log(`Viewing Order ID: ${orderID}`);
  };

  // Function to handle navigation when the "View Order" button is clicked
  const handleNavigateToOrder = (roomId, threadId) => {
    navigate(`/thread?roomIdParam=${roomId}&threadIdParam=${threadId}`);
  };

  const searchAndPressEnter = (e) => {
    if (e.key === "Enter") {
      setIsSearchTriggered(true);
      setCurrentPage(1);
      fetchUserOrder(searchTerm, 1);
    }
  };

  const handleInputChange = (e) => {
    setIsSearchTriggered(true);
    const term = e.target.value;
    setSearchTerm(term);

    if (term.trim() === "") {
      setIsSearchTriggered(false);
      setCurrentPage(1);
      setSearchTerm("");
      fetchUserOrder(term, 1);
    }
  };

  const clearFilters = () => {
    setIsSearchTriggered(false);
    setSearchTerm("");
    setCurrentPage(1); 
    setSearchParams({ page: 1, search: "" }); 
    fetchUserOrder("", 1);
  };

  const onRetry = () => {
    clearFilters();
  };
  
  return (
    <div className="flex flex-col flex-grow max-h-screen min-w-[340px] max-sm:absolute max-sm:bottom-0 max-sm:top-0 max-sm:left-0 max-sm:right-0 overflow-x-hidden">

      <div className="text-primary font-roboto font-semibold text-[24px] pb-4 ml-[24px] lg:mt-[24px] mt-[104px]">
        <h2>In Progress</h2>
      </div>

      <div className="bg-[#f9fafb] text-[13.5px] font-normal px-[12px] py-[8px] border border-[#e0e0e0] flex items-center justify-center gap-[6px] mt-[20px] mx-auto w-fit">
        <span className="text-[#27ae60] font-semibold flex items-center gap-[5px]">
          <span className="text-[#27ae60]">●</span>
          <span className=""> Order In Progress</span>
        </span>
        <span>–</span> 
        <span>These are orders paid for successfully and a writer is currently working on them.</span>
      </div>

      <div className="flex flex-col flex-grow overflow-y-auto">
        <div className="flex flex-col relative flex-grow font-inter mt-[32px] ml-[24px] mr-[24px] lg:mx-[48px] mb-[48px] border border-medium-gray rounded-lg pt-8 pb-0 px-[16px] lg:px-[px] xl:px-[px]   md:pt-10 md:-pb-5   max-w-full md:max-w-[2200px] w-auto mx-auto">
          <div className="flex flex-row  md:mb-0 lg:mb-0">
            <div
              className="flex flex-row sm:mb-4
                            gap-y-2 
                            gap-x-0 lg:text-level-3 md:text-level-4 sm:text-level-5 text-gray leading-[19.36px] h-10 font-normal max-460-br:w-full"
            >
              <div className="flex w-full items-center border border-medium-gray rounded-lg px-2 py-2 bg-white focus:outline-none ">
                <IoSearchSharp className="w-[34px] h-[40px] text-gray rounded-[3px] py-[3px] px-[5px]" />

                <input
                  type="text"
                  placeholder="Search Orders by Order ID"
                  className="ml-1 w-full text-gray font-inter text-level-4  focus:outline-none"
                  value={searchTerm}
                  onChange={handleInputChange}
                  onKeyDown={searchAndPressEnter}
                />
              </div>
            </div>
          </div>

          {/* Table for large screens */}
          <div className="hidden xl:block">
            <div className="flex flex-col flex-grow mt-20 mb-12 460-br:mt-12 sm:mt-0">
              <OrderTable
                tableName={orderStatus.ORDER_IN_PROGRESS}
                orders={pendingOrders}
                onViewOrder={handleNavigateToOrder}
                ordersPerPage={itemsPerPage}
                columnNames={{
                  orderId: "Order ID",
                  subject: "Subject",
                  price: "Price",
                  orderStatus: "Order Status",
                  created: "Created",
                  dueDate: "Due Date",
                  actions: "Actions",
                }}
                currentPage={currentPage}
                pagination={getPagination}
                onPageChange={handlePageChange}
                buttonText="View Order"
                loading={isUserOrdersFetching}
                onRetry={onRetry}
                error={userOrdersError}
              />
            </div>
          </div>

          {/* CardComponent for small screens */}
          <div className="xl:hidden">
            <div className="flex flex-col flex-grow  mt-4 sm:mt-0 460-br:mt-12 ">
              <>
                {
                   // display order not found message if there are no orders user search
                   ((searchTerm && pendingOrders.length === 0) || userOrdersError) ? (
                    <FetchingError 
                      error={userOrdersError} 
                      isEmptyRecords={pendingOrders.length === 0} 
                      onRetry={onRetry}
                    />  
                  ) : (
                    <Card
                      buttonType="View Order"
                      orderStatusFilter={null}
                      currentOrders={pendingOrders}
                      currentPage={currentPage}
                      pagination={getPagination}
                      onPageChange={handlePageChange}
                      ordersPerPage={itemsPerPage}
                      onViewOrder={handleNavigateToOrder}
                      loading={isUserOrdersFetching}
                      className="absolute -bottom-4 self-center bg-white "
                    />
                  )
                }
              </>
            </div>
          </div>

          {/* Pagination Controls */}
          <Pagination
            totalItems={getPagination.totalDocuments || 0}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 bg-white "
          />
        </div>
      </div>
    </div>
  );
};

export default InOProgressOrders;
